import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";

export default function ClientRates({ site }) {
  const rates = site.clientRates;
  if (!rates) return null;

  return (
    <section
      id="rates"
      data-testid="care-rates"
      className="py-24 md:py-32 bg-[#FDFBF7] border-t border-[#D5DACF]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <span className="eyebrow">For families & clients</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.03] text-[#1A2621]">
            Honest rates,
            <span className="italic text-[#284A3B]"> aligned with</span> the{" "}
            {site.region} market.
          </h2>
          <p className="mt-6 text-lg text-[#4A5D54] leading-relaxed max-w-md">
            {rates.note}
          </p>

          <a
            href="#contact"
            data-testid="rates-contact-cta"
            className="mt-8 inline-flex items-center gap-2 bg-[#284A3B] hover:bg-[#1C352A] text-[#FDFBF7] rounded-full px-7 py-3.5 text-sm font-medium transition-colors group"
          >
            Request a Free In-Home Assessment
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <div className="rounded-2xl border border-[#D5DACF] bg-white overflow-hidden">
            <div className="hidden md:grid grid-cols-12 px-7 py-4 bg-[#E6EAE3] text-[11px] uppercase tracking-[0.22em] text-[#4A5D54]">
              <div className="col-span-5">Service</div>
              <div className="col-span-4">Minimum</div>
              <div className="col-span-3 text-right">Starting</div>
            </div>
            <ul className="divide-y divide-[#D5DACF]">
              {rates.items.map((r, i) => (
                <li
                  key={r.label}
                  data-testid={`rate-item-${i}`}
                  className="px-6 md:px-7 py-6 md:py-7 grid md:grid-cols-12 gap-3 md:gap-6 items-center hover:bg-[#F3EFE9]/60 transition-colors"
                >
                  <div className="md:col-span-5 font-serif-display text-xl md:text-2xl text-[#1A2621]">
                    {r.label}
                  </div>
                  <div className="md:col-span-4 text-sm text-[#4A5D54]">
                    {r.min}
                  </div>
                  <div className="md:col-span-3 md:text-right">
                    <div className="font-serif-display text-2xl md:text-3xl text-[#284A3B] leading-none">
                      from ${r.from}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#4A5D54] mt-1">
                      per {r.unit}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex items-start gap-3 text-sm text-[#4A5D54] max-w-2xl">
            <Check size={16} className="mt-0.5 text-[#C86A51] shrink-0" />
            <p>{rates.disclaimer}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
